import React from 'react';
import type {
  ActivityDecision,
  ClientStatus,
  McpEnvironment,
  McpRepresentation,
  PolicyEffect,
  RiskLevel,
  SelficCapabilityKind,
} from '../governedMcpTypes';

function badge(className: string, children: React.ReactNode) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}>
      {children}
    </span>
  );
}

export function EnvBadge({ environment }: { environment: McpEnvironment }) {
  if (environment === 'production') {
    return badge('bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300', 'Production');
  }
  if (environment === 'test') {
    return badge('bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300', 'Test');
  }
  return badge('bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300', 'Development');
}

export function StatusDot({ active, label }: { active: boolean; label?: string }) {
  const text = label ?? (active ? 'Active' : 'Inactive');
  return badge(
    active ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-500',
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
      {text}
    </span>
  );
}

export function StatusBadge({
  status,
}: {
  status: 'active' | 'disabled' | 'revoked' | 'draft' | 'published' | boolean;
}) {
  const value = typeof status === 'boolean' ? (status ? 'active' : 'disabled') : status;
  if (value === 'active' || value === 'published') {
    return badge('bg-emerald-50 text-emerald-700', value === 'published' ? 'Published' : 'Active');
  }
  if (value === 'revoked') return badge('bg-red-50 text-red-700', 'Revoked');
  if (value === 'draft') return badge('bg-amber-50 text-amber-700', 'Draft');
  return badge('bg-neutral-100 text-neutral-600', 'Disabled');
}

export function ClientStatusBadge({ status }: { status: ClientStatus }) {
  return <StatusBadge status={status} />;
}

export function RiskBadge({ level }: { level: RiskLevel }) {
  const map: Record<RiskLevel, string> = {
    low: 'bg-sky-50 text-sky-700',
    medium: 'bg-amber-50 text-amber-700',
    high: 'bg-orange-50 text-orange-700',
    critical: 'bg-red-50 text-red-700',
  };
  return badge(map[level], level.charAt(0).toUpperCase() + level.slice(1));
}

export function DecisionBadge({ decision }: { decision: ActivityDecision }) {
  const map: Record<ActivityDecision, { cls: string; label: string }> = {
    allowed: { cls: 'bg-emerald-50 text-emerald-700', label: 'Allowed' },
    denied: { cls: 'bg-red-50 text-red-700', label: 'Denied' },
    approval_required: { cls: 'bg-amber-50 text-amber-700', label: 'Approval required' },
    failed: { cls: 'bg-neutral-100 text-neutral-600', label: 'Failed' },
  };
  return badge(map[decision].cls, map[decision].label);
}

export function EffectBadge({ effect }: { effect: PolicyEffect }) {
  if (effect === 'allow') return badge('bg-emerald-50 text-emerald-700', 'Allow');
  if (effect === 'deny') return badge('bg-red-50 text-red-700', 'Deny');
  return badge('bg-amber-50 text-amber-700', 'Require approval');
}

export function KindBadge({ kind }: { kind: SelficCapabilityKind }) {
  const labels: Record<SelficCapabilityKind, string> = {
    information: 'Information',
    search: 'Search',
    action: 'Action',
    process: 'Process',
    external_mcp: 'External MCP',
  };
  return badge('bg-indigo-50 text-indigo-700', labels[kind]);
}

export function RepresentationBadge({ value }: { value: McpRepresentation }) {
  const labels: Record<McpRepresentation, string> = {
    resource: 'Resource',
    tool: 'Tool',
    task: 'Task',
  };
  return badge('bg-violet-50 text-violet-700', labels[value]);
}
